import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, Link, Navigate } from "react-router-dom";
import {
  ArrowRight, BellRing, Camera, Check, ClipboardList, Edit3, KeyRound, LogIn,
  Minus, Plus, ShieldAlert, Settings, ShoppingBag, Sparkles, Tag, Trash2, UserPlus, UserRound, Users, X,
} from "lucide-react";
import ProfileModal, { ProfileButton } from "@/Profile";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API, withCredentials: true });
const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
const money = (n) => inr.format(Number(n) || 0);
const GST_RATE = 0.18;
const computeTotals = (items, couponDiscountPercent = 0) => {
  const subtotal = items.reduce((a, x) => a + x.price * x.qty, 0);
  const rate = Math.max(0, Math.min(Number(couponDiscountPercent || 0) / 100, 1));
  const discount = subtotal * rate;
  const discounted = subtotal - discount;
  const gst = discounted * GST_RATE;
  return { subtotal, discount, discounted, gst, total: discounted + gst };
};
const imgUrl = (src) => (src && src.startsWith("/api/") ? `${BACKEND}${src}` : src);

// dd/mm/yy for all order/advisor date columns.
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
};

function ConfirmDialog({ title, message, confirmLabel = "Confirm", danger = false, onConfirm, onCancel }) {
  return (
    <div className="modal-backdrop" onClick={onCancel} data-testid="confirm-dialog">
      <div className="confirm-box" onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        <p>{message}</p>
        <div className="confirm-actions">
          <button className="ghost-btn" onClick={onCancel} data-testid="confirm-cancel">Cancel</button>
          <button
            className={danger ? "danger-btn" : "primary-btn"}
            onClick={onConfirm}
            data-testid="confirm-ok"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}


function PermissionsModal({ state, onSave, onClose }) {
  const [perms, setPerms] = useState(state.perms);
  const toggle = (k) => setPerms((p) => ({ ...p, [k]: !p[k] }));
  const PERM_LABELS = {
    view_stock: "View stock",
    change_stock: "Change stock",
    view_price: "View price",
    change_price: "Change price",
    view_customers: "View customers",
    edit_customers: "Edit customers",
    view_customer_history: "View customer history",
    view_enquiries: "View enquiries",
    update_enquiry_status: "Update enquiry status",
    complete_enquiry: "Complete enquiry",
    close_enquiry: "Close enquiry",
    add_product: "Add products",
    edit_product: "Edit products",
    delete_product: "Delete products",
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="advisor-drawer"
        style={{ backgroundColor: "#1a1408", color: "#f4ebd4" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="permissions-modal"
      >
        <button className="close" onClick={onClose} data-testid="permissions-close"><X /></button>
        <div className="drawer-head">
          <div>
            <p className="eyebrow" style={{ color: "#f5d67c" }}>{state.advisor.specialty}</p>
            <h2 style={{ color: "#f7ecc9", font: "500 24px 'Playfair Display'", margin: "4px 0" }}>
              {state.advisor.name} — Permissions
            </h2>
            <p className="profile-subtle" style={{ color: "#dccd9c" }}>
              Tick what this salesman can do. Enforced on every API call.
            </p>
          </div>
        </div>
        <div className="perms-grid" data-testid="permissions-grid">
          {Object.entries(PERM_LABELS).map(([k, label]) => (
            <label className="perm-row" key={k}>
              <input
                type="checkbox"
                checked={!!perms[k]}
                onChange={() => toggle(k)}
                data-testid={`perm-${k}`}
              />
              {label}
            </label>
          ))}
        </div>
        <div className="perms-templates">
          <span style={{ color: "#dccd9c", fontSize: 11, alignSelf: "center", marginRight: 8, textTransform: "uppercase", letterSpacing: 1 }}>
            Apply template:
          </span>
          {state.templates.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => onSave(perms, t)}
              data-testid={`template-${t}`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="confirm-actions" style={{ marginTop: 22 }}>
          <button className="ghost-btn" onClick={onClose} data-testid="permissions-cancel">Cancel</button>
          <button className="primary-btn" onClick={() => onSave(perms)} data-testid="permissions-save">
            Save permissions <Check size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}

function ToastStack({ toasts }) {
  return (
    <div className="toast-stack" data-testid="toast-stack">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast toast-${t.kind}`}
          data-testid={`toast-${t.kind}`}
        >
          <Check size={16} /> <span>{t.text}</span>
        </div>
      ))}
    </div>
  );
}

function variantKey(v, index = 0) {
  return String(v?.variant_id || v?.sku || v?.model_number || v?.model || `variant-${index}`);
}

function VariantPicker({ product, value, onChange }) {
  if (!product.variants || product.variants.length === 0) return null;
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="variant-select"
      data-testid={`variant-select-${product.id}`}
      onClick={(e) => e.stopPropagation()}
    >
      {product.variants.map((v, i) => {
        const id = variantKey(v, i);
        const model = v.model_number || v.model || "";
        return (
          <option key={id} value={id}>
            {v.wattage || "—"}{v.size ? ` | ${v.size}` : ""}{model ? ` | ${model}` : ""} — ₹{Number(v.price || 0).toLocaleString("en-IN")}
          </option>
        );
      })}
    </select>
  );
}

function MyOrdersModal({ onClose }) {
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    api.get("/my/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, []);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="advisor-drawer"
        onClick={(e) => e.stopPropagation()}
        data-testid="my-orders-modal"
      >
        <button className="close" onClick={onClose} data-testid="my-orders-close"><X /></button>
        <div className="drawer-head">
          <div>
            <p className="eyebrow">Your inquiries</p>
            <h2>My orders</h2>
          </div>
        </div>
        {orders === null && <p className="profile-subtle">Loading…</p>}
        {orders && orders.length === 0 && (
          <p className="profile-subtle" data-testid="my-orders-empty">
            You haven't placed any inquiries yet.
          </p>
        )}
        {orders && orders.length > 0 && (
          <div className="advisor-order-list">
            {orders.map((o) => (
              <div className="advisor-order" key={o.id} data-testid={`my-order-${o.id}`}>
                <div>
                  <b>{o.id}</b>
                  <small>{fmtDate(o.created_at)}</small>
                  <small>{(o.items || []).length} item(s)</small>
                </div>
                <div>
                  <span className={`pill status-${(o.status || "").toLowerCase().replace(" ","-")}`}>
                    {o.status}
                  </span>
                </div>
                <div>
                  <b>{inr.format(Number(o.total) || 0)}</b>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const BrandMark = ({ size = 34 }) => (
  <span className="brand-mark" style={{ height: size, width: size }}>
    <img src="/logo.png" alt="LUMINEST LIGHTS" />
  </span>
);

const GoogleG = () => (
  <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.7 1.22 9.19 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);


function Header({ cartCount, onCart, user, onAuth, onLogout, onDashboard, onAdmin, onProfile, onOrders }) {
  return (
    <header className="topbar">
      <Link to="/" className="brand" data-testid="brand-home">
        <BrandMark />
        <span>LUMINEST <em>LIGHTS</em></span>
      </Link>
      <nav>
        <a href="#collection" data-testid="collection-nav-link">Collection</a>
      </nav>
      <div className="top-actions">
        {user?.role === "admin" && (
          <button className="text-btn" onClick={onAdmin} data-testid="admin-catalog-button">
            <Settings size={16} /> Catalog manager
          </button>
        )}
        {(user?.role === "sales" || user?.role === "admin") && (
          <button className="text-btn" onClick={onDashboard} data-testid="sales-dashboard-button">
            <ClipboardList size={16} /> Inquiries
          </button>
        )}
        {user?.role === "customer" && onOrders && (
          <button className="text-btn" onClick={onOrders} data-testid="my-orders-button">
            <ClipboardList size={16} /> My orders
          </button>
        )}
        <ProfileButton user={user} onClick={onProfile} />
        <button className="account-btn" onClick={user ? onLogout : onAuth} data-testid="account-button">
          <UserRound size={17} />{user ? "Sign out" : "Sign in"}
        </button>
        <button className="cart-btn" onClick={onCart} data-testid="cart-button">
          <ShoppingBag size={18} /><b data-testid="cart-count">{cartCount}</b>
        </button>
      </div>
    </header>
  );
}

const IMG_FALLBACK =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 400'>
      <rect width='400' height='400' fill='#eaebe5'/>
      <g fill='none' stroke='#9aa199' stroke-width='6' stroke-linecap='round'>
        <path d='M200 90c40 0 70 30 70 68 0 26-15 48-38 60v22h-64v-22c-23-12-38-34-38-60 0-38 30-68 70-68z'/>
        <path d='M172 258h56'/><path d='M180 278h40'/>
      </g>
      <text x='200' y='340' text-anchor='middle' font-family='Inter,Arial' font-size='18' fill='#8a918a'>LED FITTING</text>
    </svg>`
  );

function ProductCard({ p, onOpen, onAdd, showStock }) {
  const hasVariants = (p.variants || []).length > 0;
  const [variantId, setVariantId] = useState(hasVariants ? variantKey(p.variants[0], 0) : "");
  useEffect(() => {
    setVariantId(hasVariants ? variantKey(p.variants[0], 0) : "");
  }, [p.id, hasVariants, p.variants]);
  const variant = hasVariants
    ? (p.variants.find((v, i) => variantKey(v, i) === variantId) || p.variants[0])
    : null;
  const effectivePrice = variant ? Number(variant.price || 0) : Number(p.price || 0);
  return (
    <article className="product" data-testid={`product-card-${p.id}`} onClick={() => onOpen(p)}>
      <div className="product-image">
        <img
          src={imgUrl(p.image)}
          alt={p.name}
          onError={(e) => { if (e.currentTarget.src !== IMG_FALLBACK) e.currentTarget.src = IMG_FALLBACK; }}
        />
        {showStock && <span className="stock" data-testid={`stock-${p.id}`}>{(p.stock ?? 0) - (p.reserved_qty ?? 0)} available</span>}
        <button
          className="quick-add"
          onClick={(e) => { e.stopPropagation(); onAdd(p, variant); }}
          data-testid={`add-${p.id}-button`}
        >
          <Plus size={19} />
        </button>
      </div>
      <div className="product-info">
        <div>
          {p.category ? <p>{p.category}</p> : null}
          <h3>{p.name}</h3>
          {hasVariants && (
            <VariantPicker product={p} value={variantId} onChange={setVariantId} />
          )}
        </div>
        <strong data-testid={`price-${p.id}`}>
          {money(effectivePrice)}
          <em className="ex-gst"> + GST</em>
        </strong>
      </div>
      <div className="spec-row">
        {p.wattage && !hasVariants ? <span>{p.wattage}</span> : null}
        {p.color ? <span>{p.color}</span> : null}
        {p.size ? <span>{p.size}</span> : null}
      </div>
    </article>
  );
}

function Home({ cart, setCart, user, onAuth, onLogout, onProfile, onOrders, toast }) {
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("All");
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [ordered, setOrdered] = useState(null);
  const [selectedSales, setSelectedSales] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponStatus, setCouponStatus] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  const lockedAdvisor = useMemo(
    () => (user?.locked_advisor_id ? sales.find((s) => s.id === user.locked_advisor_id) : null),
    [sales, user],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Promise.all([api.get("/products"), api.get("/sales-people")])
      .then(([p, s]) => {
        setProducts(p.data);
        setSales(s.data);
        setSelectedSales(s.data[0]?.id || "");
      })
      .catch(console.error);
  }, []);

  const cats = useMemo(() => ["All", ...Array.from(new Set(products.map((x) => x.category)))], [products]);
  const visible = filter === "All" ? products : products.filter((x) => x.category === filter);
  const totals = computeTotals(cart, couponDiscount);

  const add = (p, variant = null) => {
    const effectivePrice = variant ? Number(variant.price || 0) : Number(p.price || 0);
    const variantIndex = variant && p.variants ? p.variants.findIndex((v) => v === variant) : -1;
    const variantW = variant?.wattage || "";
    const variantSize = variant?.size || "";
    const variantModel = variant?.model_number || variant?.model || "";
    const variantSku = variant?.sku || "";
    const variantId = variant ? variantKey(variant, variantIndex) : "";
    // Unique cart line per product + selected variant.
    const key = `${p.id}::${variantId}`;
    setCart((c) => {
      const found = c.find((x) => x.lineKey === key);
      if (found) return c.map((x) => (x.lineKey === key ? { ...x, qty: x.qty + 1 } : x));
      return [...c, {
        ...p, qty: 1, price: effectivePrice, lineKey: key,
        variant_id: variant?.variant_id || "",
        variant_index: variantIndex,
        variant_wattage: variantW,
        variant_size: variantSize,
        variant_model_number: variantModel,
        variant_sku: variantSku,
        variant_stock: variant?.stock ?? null,
      }];
    });
    if (toast) toast(variant ? `${p.name} (${variantW || variantSize || variantModel || "variant"}) added to cart` : `${p.name} added to cart`);
  };

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) { setCouponStatus("Enter a coupon code"); return; }
    setCouponBusy(true);
    setCouponStatus("");
    try {
      const r = await api.post("/coupons/validate", { code });
      setCouponCode(r.data.code);
      setCouponDiscount(Number(r.data.discount_percent || 0));
      setCouponStatus(`Coupon applied: ${r.data.discount_percent}% off`);
    } catch (e) {
      setCouponDiscount(0);
      setCouponStatus(e.response?.data?.detail || "Invalid coupon code");
    } finally {
      setCouponBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const advisorId = lockedAdvisor?.id || selectedSales;
    if (!advisorId) { alert("Please pick a salesman"); return; }
    if (cart.length === 0) { alert("Please add at least one product"); return; }
    const f = new FormData(e.currentTarget);
    setBusy(true);
    try {
      // Backend automatically emails the complete inquiry to luminestlights@gmail.com.
      const r = await api.post("/orders", {
        customer_name: f.get("name"),
        phone: f.get("phone"),
        email: f.get("email") || "",
        address: f.get("address"),
        gst_number: f.get("gst_number") || "",
        sales_person_id: advisorId,
        coupon_code: couponCode.trim().toUpperCase(),
        items: cart.map((x) => ({
          product_id: x.id, name: x.name, qty: x.qty, price: x.price,
          variant_id: x.variant_id || "",
          variant_index: x.variant_index ?? -1,
          variant_wattage: x.variant_wattage || "",
          variant_size: x.variant_size || "",
          variant_model_number: x.variant_model_number || "",
          variant_sku: x.variant_sku || "",
          variant_stock: x.variant_stock ?? null,
        })),
        notes: f.get("notes") || "",
      });
      setOrdered(r.data);
      setCart([]);
      setCouponCode("");
      setCouponDiscount(0);
      setCouponStatus("");
      setShowCheckout(false);
      setShowCart(false);
      // Keep customer logged in; they can place more inquiries right away.
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      let message = detail || "Could not submit inquiry";
      if (!err.response) {
        message = "Could not connect to the server. Please check that the backend is running and REACT_APP_BACKEND_URL is correct.";
      } else if (status >= 500) {
        message = `Server error (${status}). Please check the backend terminal/logs.`;
      }
      console.error("Inquiry submission failed:", { status, detail, error: err });
      alert(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Header
        cartCount={cart.reduce((a, x) => a + x.qty, 0)}
        onCart={() => setShowCart(true)}
        user={user}
        onAuth={onAuth}
        onLogout={onLogout}
        onDashboard={() => (window.location.href = "/sales")}
        onAdmin={() => (window.location.href = "/admin")}
        onProfile={onProfile}
        onOrders={onOrders}
      />
      <main>
        <section className="hero">
          <div className="hero-copy">
            <p className="eyebrow"><Sparkles size={15} /> Coupon savings · 18% GST</p>
            <h1>Light that makes<br /><em>every home shine.</em></h1>
            <p className="hero-text">
              An honest LED lighting catalogue — bulbs, panels, floodlights, strips and more.
              Enter a coupon code from your salesman at checkout to unlock your special discount.
            </p>
            <a href="#collection" className="primary-btn" data-testid="explore-collection-button">
              Explore collection <ArrowRight size={17} />
            </a>
          </div>
          <div className="hero-image">
            <img
              src="https://images.unsplash.com/photo-1776433371815-71f4eda21f02?auto=format&fit=crop&w=1400&q=85"
              alt="LUMINEST LIGHTS showroom"
            />
            <div className="hero-caption">
              <span className="live-dot" /> Live catalogue <b>Prices in ₹</b>
            </div>
          </div>
        </section>

        <section className="intro-band">
          <p>Bright rooms.<br /><strong>Warmer moments.</strong></p>
          <div>
            <span className="line" />
            <span>
              From a single bulb to a full building,<br />
              LUMINEST LIGHTS keeps every corner glowing.
            </span>
          </div>
        </section>

        <section id="collection" className="collection">
          <div className="section-head">
            <div>
              <p className="eyebrow">The collection</p>
              <h2>Find your glow.</h2>
            </div>
            <div className="filters">
              {cats.map((c) => (
                <button
                  key={c}
                  className={filter === c ? "filter active" : "filter"}
                  onClick={() => setFilter(c)}
                  data-testid={`filter-${c.toLowerCase().replaceAll(" ", "-")}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="product-grid">
            {visible.map((p) => (
              <ProductCard key={p.id} p={p} onOpen={setSelected} onAdd={add} showStock={user?.role === "sales" || user?.role === "admin"} />
            ))}
            {visible.length === 0 && (
              <div className="empty" data-testid="empty-catalog">
                <ShoppingBag size={30} />
                <p>No products in this category yet.</p>
              </div>
            )}
          </div>
        </section>

        <footer className="site-foot">
          <div><BrandMark size={28} /><span>LUMINEST LIGHTS</span></div>
          <p>© {new Date().getFullYear()} LUMINEST LIGHTS · Bright quality lighting for every space</p>
        </footer>
      </main>

      {selected && (
        <div className="modal-backdrop" onClick={() => setSelected(null)}>
          <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close" onClick={() => setSelected(null)} data-testid="product-modal-close"><X /></button>
            <img
              src={imgUrl(selected.image)}
              alt={selected.name}
              onError={(e) => { if (e.currentTarget.src !== IMG_FALLBACK) e.currentTarget.src = IMG_FALLBACK; }}
            />
            <div className="detail-copy">
              <p className="eyebrow">{selected.category}</p>
              <h2>{selected.name}</h2>
              <strong className="detail-price" data-testid="detail-price">{money(selected.price)}</strong>
              <p>{selected.description}</p>
              <div className="details">
                {["wattage", "color", "size", "warranty"].map((k) => (
                  <div key={k}><small>{k}</small><b>{selected[k]}</b></div>
                ))}
              </div>
              <button
                className="primary-btn full"
                onClick={() => { add(selected); setSelected(null); setShowCart(true); }}
                data-testid="modal-add-to-cart-button"
              >
                Add to inquiry <ShoppingBag size={17} />
              </button>
            </div>
          </div>
        </div>
      )}

      {showCart && (
        <div className="drawer-backdrop" onClick={() => setShowCart(false)}>
          <aside className="cart-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-head">
              <h2>Your inquiry</h2>
              <button className="close" onClick={() => setShowCart(false)} data-testid="cart-close-button"><X /></button>
            </div>
            {cart.length === 0 ? (
              <div className="empty">
                <ShoppingBag size={34} />
                <p>Your inquiry is empty.<br />Add a light to get started.</p>
              </div>
            ) : (
              <>
                <div className="cart-lines">
                  {cart.map((x) => (
                    <div className="cart-line" key={x.id}>
                      <img
                        src={imgUrl(x.image)}
                        alt=""
                        onError={(e) => { if (e.currentTarget.src !== IMG_FALLBACK) e.currentTarget.src = IMG_FALLBACK; }}
                      />
                      <div>
                        <b>{x.name}</b>
                        <small>{money(x.price)} · {x.qty} selected</small>
                        <div className="qty">
                          <button
                            onClick={() =>
                              setCart((c) => c.map((y) => (y.id === x.id ? { ...y, qty: Math.max(1, y.qty - 1) } : y)))
                            }
                            data-testid={`decrease-${x.id}-button`}
                          ><Minus size={13} /></button>
                          <span>{x.qty}</span>
                          <button onClick={() => add(x)} data-testid={`increase-${x.id}-button`}><Plus size={13} /></button>
                          <button
                            className="cart-remove"
                            onClick={() => setCart((c) => c.filter((y) => y.id !== x.id))}
                            data-testid={`remove-${x.id}-button`}
                            aria-label="Remove from cart"
                          ><Trash2 size={13} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="drawer-totals">
                  <div><span>Subtotal</span><b data-testid="cart-subtotal">{money(totals.subtotal)}</b></div>
                  <div className="save"><span>Coupon discount</span><b data-testid="cart-discount">− {money(totals.discount)}</b></div>
                  <div><span>After discount</span><b>{money(totals.discounted)}</b></div>
                  <div><span>GST (18%)</span><b data-testid="cart-gst">{money(totals.gst)}</b></div>
                  <div className="grand"><span>Grand total (incl. GST)</span><b data-testid="cart-total">{money(totals.total)}</b></div>
                  <div className="save-chip" data-testid="cart-savings">{couponDiscount > 0 ? `You save ${money(totals.discount)} with coupon ${couponCode}` : "Apply your salesman coupon at checkout"}</div>
                </div>
                <button
                  className="primary-btn full"
                  onClick={() => setShowCheckout(true)}
                  data-testid="proceed-to-checkout-button"
                >
                  Choose salesman & inquire <ArrowRight size={17} />
                </button>
              </>
            )}
          </aside>
        </div>
      )}

      {showCheckout && (
        <div className="modal-backdrop">
          <form className="checkout" onSubmit={submit}>
            <div className="drawer-head">
              <div>
                <p className="eyebrow">Almost there</p>
                <h2>Tell us where to shine.</h2>
              </div>
              <button type="button" className="close" onClick={() => setShowCheckout(false)} data-testid="checkout-close-button"><X /></button>
            </div>
            {lockedAdvisor ? (
              <div className="locked-advisor" data-testid="checkout-locked-advisor">
                <div>
                  <p className="eyebrow">Your salesman</p>
                  <b>{lockedAdvisor.name}</b>
                  <small>{lockedAdvisor.specialty}</small>
                </div>
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => { setShowCheckout(false); onProfile && onProfile(); }}
                  data-testid="checkout-request-advisor-change"
                >
                  Request change
                </button>
              </div>
            ) : (
              <label>
                Your salesman
                <select value={selectedSales} onChange={(e) => setSelectedSales(e.target.value)} data-testid="sales-person-select">
                  {sales.map((s) => (
                    <option key={s.id} value={s.id}>{`${s.name} — ${s.specialty}`}</option>
                  ))}
                </select>
              </label>
            )}
            <label>Full name<input name="name" required placeholder="Your name" defaultValue={user?.name || ""} data-testid="checkout-name-input" /></label>
            <label>Phone number<input name="phone" required placeholder="+91 98765 43210" defaultValue={user?.phone || ""} data-testid="checkout-phone-input" /></label>
            <label>Email {user ? "" : <small>(optional)</small>}<input name="email" type="email" required={!!user} placeholder="you@example.com" defaultValue={user?.email || ""} data-testid="checkout-email-input" /></label>
            <label>Delivery address<textarea name="address" required placeholder="Where should we send your light?" defaultValue={user?.address || ""} data-testid="checkout-address-input" /></label>
            <label>GST number <small>(optional)</small><input name="gst_number" placeholder="22AAAAA0000A1Z5" defaultValue={user?.gst_number || ""} data-testid="checkout-gst-input" /></label>
            <label>Project note <small>(optional)</small><textarea name="notes" placeholder="Room, timeline, or anything else..." data-testid="checkout-notes-input" /></label>
            <div className="coupon-box">
              <label>Coupon code <small>(given by your salesman)</small>
                <input
                  value={couponCode}
                  onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponDiscount(0); setCouponStatus(""); }}
                  placeholder="ENTER COUPON CODE"
                  autoComplete="off"
                  data-testid="coupon-code-input"
                />
              </label>
              <button type="button" className="ghost-btn" onClick={applyCoupon} disabled={couponBusy} data-testid="apply-coupon-button">
                <Tag size={15} /> {couponBusy ? "Checking…" : "Apply coupon"}
              </button>
              {couponStatus && <small className={couponDiscount > 0 ? "notice" : "error"} data-testid="coupon-status">{couponStatus}</small>}
            </div>
            <div className="checkout-totals" data-testid="checkout-totals">
              <div><span>Subtotal</span><b>{money(totals.subtotal)}</b></div>
              <div className="save"><span>Coupon discount{couponDiscount > 0 ? ` (${couponDiscount}%)` : ""}</span><b>− {money(totals.discount)}</b></div>
              <div><span>After discount</span><b>{money(totals.discounted)}</b></div>
              <div><span>GST (18%)</span><b>{money(totals.gst)}</b></div>
              <div className="grand"><span>Grand total</span><b>{money(totals.total)}</b></div>
            </div>
            <button className="primary-btn full" disabled={busy} data-testid="submit-inquiry-button">
              {busy ? "Submitting…" : "Submit inquiry"} <ArrowRight size={17} />
            </button>
          </form>
        </div>
      )}

      {ordered && (
        <div className="modal-backdrop">
          <div className="success">
            <div className="success-icon"><Check /></div>
            <p className="eyebrow">Inquiry received</p>
            <h2>We&rsquo;ll light the way.</h2>
            <p>
              Your inquiry <strong data-testid="ordered-id">{ordered.id}</strong> has been received.
              You saved <strong>{money(ordered.discount || 0)}</strong>{ordered.coupon_code ? ` with coupon ${ordered.coupon_code}` : " with your coupon"};
              grand total (incl. 18% GST): <strong data-testid="ordered-total">{money(ordered.total)}</strong>.
              Your selected salesman will follow up with you shortly.
            </p>
            <button className="primary-btn" onClick={() => setOrdered(null)} data-testid="success-close-button">
              Back to showroom <ArrowRight size={17} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function Auth({ setUser, onClose }) {
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const f = new FormData(e.currentTarget);
    try {
      const r = await api.post(`/auth/${mode}`, {
        name: f.get("name"),
        email: f.get("email"),
        password: f.get("password"),
      });
      setUser(r.data);
      onClose();
      if (r.data.role === "admin" || r.data.role === "sales") {
        window.location.href = "/sales";
      }
    } catch (x) {
      setError(x.response?.data?.detail || "Something went wrong");
    }
  };
  return (
    <div className="modal-backdrop">
      <form className="auth-modal" onSubmit={submit}>
        <button type="button" className="close" onClick={onClose} data-testid="auth-close-button"><X /></button>
        <p className="eyebrow">LUMINEST account</p>
        <h2>
          {mode === "login" && "Welcome back."}
          {mode === "register" && "Make it personal."}
        </h2>
        <p>
          {mode === "login" && "Track your lighting inquiries and reorder in one click."}
          {mode === "register" && "Save your project details for a faster inquiry next time."}
        </p>
        {mode === "register" && (
          <input name="name" required placeholder="Your name" data-testid="register-name-input" />
        )}
        <input name="email" type="email" required placeholder="Email address" data-testid="auth-email-input" />
        <input name="password" type="password" required minLength="6" placeholder="Password" data-testid="auth-password-input" />
        {error && <div className="error" data-testid="auth-error-message">{error}</div>}
        <button className="primary-btn full" data-testid="auth-submit-button">
          {mode === "login" && <>Sign in <LogIn size={17} /></>}
          {mode === "register" && <>Create account <LogIn size={17} /></>}
        </button>
        <div className="auth-divider"><span>or</span></div>
        <button
          type="button"
          className="google-btn full"
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
            // THIS BREAKS THE AUTH.
            const redirectUrl = window.location.origin + "/";
            window.location.href =
              "https://auth.emergentagent.com/?redirect=" +
              encodeURIComponent(redirectUrl);
          }}
          data-testid="google-signin-button"
        >
          <GoogleG /> Continue with Google
        </button>
        <div className="auth-links">
          <button
            type="button"
            className="switch"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            data-testid="auth-switch-button"
          >
            {mode === "login" ? "Need an account? Create one" : "Already have an account? Sign in"}
          </button>
        </div>
      </form>
    </div>
  );
}

function SalesDashboard({ user, onLogout }) {
  const [deletedOrders, setDeletedOrders] = useState([]);
const [showDeletedOrders, setShowDeletedOrders] = useState(false);
const [loadingDeletedOrders, setLoadingDeletedOrders] = useState(false);
  const [orders, setOrders] = useState([]);
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState("All");
  const isAdmin = user?.role === "admin";
  const [editingOrder, setEditingOrder] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [editItems, setEditItems] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [newItemProductId, setNewItemProductId] = useState("");
  const [newItemVariantId, setNewItemVariantId] = useState("");
  const [savingOrder, setSavingOrder] = useState(false);
  const [historyOrder, setHistoryOrder] = useState(null);
  const [orderHistory, setOrderHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const refresh = () => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/customers/stats").then((r) => setCustomers(r.data)).catch(() => {});
  };

  useEffect(() => {
    refresh();
    api.get("/products").then((r) => setCatalogProducts(r.data)).catch(() => {});
    api.get("/sales-people").then((r) => setSales(r.data)).catch(() => {});
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const salesName = (id) => (sales.find((s) => s.id === id) || {}).name || id;

  const toggleSelect = (id) => setSelectedIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const clearSelection = () => setSelectedIds(new Set());

  const changeStatus = async (id, status) => {
    setOrders((c) => c.map((o) => (o.id === id ? { ...o, status } : o)));
    try {
      await api.patch(`/orders/${id}`, { status });
      await refresh();
      window.dispatchEvent(new Event("luminest-rankings-refresh"));
    } catch (e) {
      await refresh();
      alert(e.response?.data?.detail || "Could not update inquiry status");
    }
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    try { await api.delete(`/orders/${id}`); refresh(); }
    catch (e) { alert(e.response?.data?.detail || "Only the host can delete"); }
  };
  const loadDeletedOrders = async () => {
  setLoadingDeletedOrders(true);
  try {
    const response = await api.get("/admin/trash");
    setDeletedOrders(
      response.data.filter((item) => item.collection_name === "orders")
    );
  } catch (e) {
    alert(e.response?.data?.detail || "Could not load deleted orders.");
  } finally {
    setLoadingDeletedOrders(false);
  }
};

const restoreDeletedOrder = async (trashId) => {
  if (!window.confirm("Restore this order to the Orders list?")) return;

  try {
    await api.post(`/admin/trash/restore/${trashId}`);
    await refresh();
    await loadDeletedOrders();
    alert("Order restored successfully.");
  } catch (e) {
    alert(e.response?.data?.detail || "Could not restore this order.");
  }
};

  const openEditOrder = (order) => {
    setEditingOrder(order);
    setEditDraft({
      customer_name: order.customer_name || "",
      phone: order.phone || "",
      email: order.email || "",
      address: order.address || "",
      gst_number: order.gst_number || "",
      coupon_code: order.coupon_code || "",
    });
    setEditItems((order.items || []).map((item) => ({ ...item })));
    setNewItemProductId("");
  };

  const updateEditItem = (index, field, value) => {
    setEditItems((items) => items.map((item, i) => {
      if (i !== index) return item;
      if (field === "qty") return { ...item, qty: value };
      if (field === "variant_id") {
        const product = catalogProducts.find((p) => String(p.id) === String(item.product_id));
        const variants = product?.variants || [];
        const variantIndex = variants.findIndex((v, vi) => variantKey(v, vi) === value);
        if (variantIndex < 0) return item;
        const v = variants[variantIndex];
        return {
          ...item,
          name: product.name,
          price: Number(v.price || 0),
          variant_id: v.variant_id || "",
          variant_index: variantIndex,
          variant_wattage: v.wattage || "",
          variant_size: v.size || "",
          variant_model_number: v.model_number || v.model || "",
          variant_sku: v.sku || "",
        };
      }
      return item;
    }));
  };

  const addEditItem = () => {
    const product = catalogProducts.find((p) => String(p.id) === String(newItemProductId));
    if (!product) {
      alert("Please select a product first.");
      return;
    }
    const variants = product.variants || [];
    let variant = null;
    let variantIndex = -1;
    if (variants.length) {
      variantIndex = variants.findIndex((v, i) => variantKey(v, i) === newItemVariantId);
      if (variantIndex < 0) {
        alert("Please select a product variant.");
        return;
      }
      variant = variants[variantIndex];
    }
    setEditItems((items) => [
      ...items,
      {
        product_id: product.id,
        name: product.name,
        qty: 1,
        price: Number(variant ? variant.price : product.price || 0),
        ...(variant ? {
          variant_id: variant.variant_id || "",
          variant_index: variantIndex,
          variant_wattage: variant.wattage || "",
          variant_size: variant.size || "",
          variant_model_number: variant.model_number || variant.model || "",
          variant_sku: variant.sku || "",
        } : {}),
      },
    ]);
    setNewItemProductId("");
    setNewItemVariantId("");
  };

  const saveOrderEdits = async (event) => {
    event.preventDefault();
    if (!editingOrder || savingOrder) return;

    if (editItems.some((item) =>
      !item.product_id || !item.name || !Number.isFinite(Number(item.qty)) ||
      Number(item.qty) < 1
    )) {
      alert("Please check products and quantities.");
      return;
    }

    const items = editItems.map((item) => ({
      ...item,
      qty: Number(item.qty),
    }));

    setSavingOrder(true);
    try {
      await api.patch(`/orders/${editingOrder.id}`, {
        ...editDraft,
        items,
      });
      setEditingOrder(null);
      await refresh();
      alert("Order updated successfully.");
    } catch (e) {
      alert(e.response?.data?.detail || "Could not update order.");
    } finally {
      setSavingOrder(false);
    }
  };

  const openOrderHistory = async (order) => {
    setHistoryOrder(order);
    setOrderHistory([]);
    setLoadingHistory(true);
    try {
      const response = await api.get(`/orders/${order.id}/history`);
      setOrderHistory(Array.isArray(response.data) ? response.data : (response.data.history || []));
    } catch (e) {
      alert(e.response?.data?.detail || "Could not load order history.");
      setHistoryOrder(null);
    } finally {
      setLoadingHistory(false);
    }
  };

  const bulk = async (action, status) => {
    if (selectedIds.size === 0) return;
    if (action === "delete" && !window.confirm(`Delete ${selectedIds.size} inquiries permanently?`)) return;
    try {
      await api.post("/orders/bulk", { ids: [...selectedIds], action, status });
      clearSelection();
      await refresh();
      window.dispatchEvent(new Event("luminest-rankings-refresh"));
    } catch (e) {
      alert(e.response?.data?.detail || "Bulk action failed");
    }
  };

  const newCount = orders.filter((x) => x.status === "New").length;
  const visibleOrders = statusFilter === "All"
    ? orders
    : orders.filter((o) => o.status === statusFilter);

  return (
    <div className="sales-page">
      <Header
        cartCount={0}
        onCart={() => {}}
        user={user}
        onAuth={() => {}}
        onLogout={onLogout}
        onDashboard={() => {}}
        onAdmin={() => (window.location.href = "/admin")}
      />
      <main className="sales-main">
        <div className="sales-heading">
          <div>
            <p className="eyebrow">
              {user?.role === "admin" ? "LUMINEST host desk" : "LUMINEST salesman desk"}
            </p>
            <h1>Hello, <em>{user?.name?.split(" ")[0] || "team"}.</em></h1>
            <p>
              {user?.role === "admin"
                ? "Every inquiry submitted on the site lands here."
                : "You'll see every inquiry a customer chose you for."}
            </p>
          </div>
          <Link to="/" className="primary-btn" data-testid="back-to-showroom-button">
            Back to showroom <ArrowRight size={17} />
          </Link>
        </div>

        {newCount > 0 && (
          <div className="notify" data-testid="new-inquiry-notification">
            <BellRing size={16} /> {newCount} new {newCount === 1 ? "inquiry" : "inquiries"} waiting for action
          </div>
        )}

        <div className="stats">
          <div><small>Open inquiries</small><b data-testid="stat-open">{orders.filter((x) => x.status !== "Closed").length}</b></div>
          <div><small>New</small><b data-testid="stat-new">{newCount}</b></div>
          <div><small>Closed</small><b data-testid="stat-closed">{orders.filter((x) => x.status === "Closed").length}</b></div>
        </div>

        {selectedIds.size > 0 && (
          <div className="bulk-toolbar" data-testid="bulk-toolbar">
            <span>{selectedIds.size} selected</span>
            <div className="bulk-actions">
              <button className="ghost-btn" onClick={() => bulk("status", "New")} data-testid="bulk-mark-open">Mark New</button>
              <button className="ghost-btn" onClick={() => bulk("status", "Confirmed")} data-testid="bulk-mark-confirmed">Mark Confirmed</button>
              <button className="ghost-btn" onClick={() => bulk("status", "Closed")} data-testid="bulk-mark-closed">Mark Closed</button>
              {isAdmin && (
                <button className="danger-btn" onClick={() => bulk("delete")} data-testid="bulk-delete">
                  <Trash2 size={13} /> Delete
                </button>
              )}
              <button className="link-btn" onClick={clearSelection} data-testid="bulk-clear">Clear</button>
            </div>
          </div>
        )}

        <section className="orders">
          <div className="section-head">
            <div>
              <p className="eyebrow">Live queue</p>
              <h2>Customer inquiries</h2>
            </div>
            {isAdmin && (
  <button
    className="ghost-btn"
    onClick={async () => {
      const next = !showDeletedOrders;
      setShowDeletedOrders(next);
      if (next) await loadDeletedOrders();
    }}
    data-testid="show-deleted-orders"
  >
    <Trash2 size={14} />
    {showDeletedOrders ? "Hide Deleted Orders" : "Deleted Orders"}
  </button>
)}
            <div className="filter-row">
              <label className="filter-label">
                Status
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  data-testid="order-status-filter"
                >
                  <option value="All">All ({orders.length})</option>
                  <option value="New">New ({orders.filter(o=>o.status==="New").length})</option>
                  <option value="In Review">In Review</option>
                  <option value="Quoted">Quoted</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="Closed">Closed ({orders.filter(o=>o.status==="Closed").length})</option>
                </select>
              </label>
            </div>
          </div>
          {visibleOrders.length === 0 ? (
            <div className="empty-orders" data-testid="empty-orders">
              <ClipboardList size={30} />
              <p>{orders.length === 0
                ? "No inquiries yet. They'll appear here the moment a customer submits."
                : `No inquiries with status "${statusFilter}".`}</p>
            </div>
          ) : (
            <div className="order-table">
              <div className="table-head">
                <span>
                  <input
                    type="checkbox"
                    checked={selectedIds.size === visibleOrders.length && visibleOrders.length > 0}
                    onChange={() => setSelectedIds((s) => s.size === visibleOrders.length ? new Set() : new Set(visibleOrders.map((o) => o.id)))}
                    data-testid="select-all-orders"
                  />
                </span>
                <span>Inquiry</span><span>Customer</span><span>Items</span><span>Salesman</span><span>Total</span><span>Status</span><span></span>
              </div>
              {visibleOrders.map((o) => (
                <div className="table-row" key={o.id} data-testid={`order-row-${o.id}`}>
                  <span>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(o.id)}
                      onChange={() => toggleSelect(o.id)}
                      data-testid={`select-order-${o.id}`}
                    />
                  </span>
                  <span>
                    <b>{o.id}</b>
                    <small>{fmtDate(o.created_at)}</small>
                  </span>
                  <span>
                    {o.customer_name}
                    <small>{o.phone}</small>
                    <small>{o.address}</small>
                    {o.email ? <small>{o.email}</small> : null}
                    {o.gst_number ? <small>GSTIN: {o.gst_number}</small> : null}
                  </span>
                  <span>
                    {(o.items || []).map((it, itemIndex) => {
                      const variantParts = [
                        it.variant_wattage && `Wattage: ${it.variant_wattage}`,
                        it.variant_size && `Size: ${it.variant_size}`,
                        it.variant_model_number && `Model: ${it.variant_model_number}`,
                        it.variant_sku && `SKU: ${it.variant_sku}`,
                      ].filter(Boolean);
                      const variantKey =
                        it.variant_id ||
                        it.variant_sku ||
                        it.variant_model_number ||
                        (Number(it.variant_index) >= 0 ? `index-${it.variant_index}` : `base-${itemIndex}`);

                      return (
                        <div key={`${it.product_id}-${variantKey}`}>
                          <b>{it.qty} × {it.name}</b>
                          <small>{money(it.price)} each</small>
                          {variantParts.length > 0 ? (
                            <small className="item-variant-details">
                              Variant: {variantParts.join(" · ")}
                            </small>
                          ) : (
                            <small className="item-variant-details">Base product</small>
                          )}
                        </div>
                      );
                    })}
                  </span>
                  <span>{salesName(o.sales_person_id)}</span>
                  <span data-testid={`order-total-${o.id}`}>
                    <b>{money(o.total ?? 0)}</b>
                    <small>Subtotal {money(o.subtotal ?? 0)}</small>
                    {o.discount ? <small className="save">Discount − {money(o.discount)}</small> : null}
                    <small>GST {money(o.gst ?? 0)}</small>
                  </span>
                  <select
                    value={o.status}
                    onChange={(e) => changeStatus(o.id, e.target.value)}
                    data-testid={`order-status-${o.id}`}
                  >
                    <option>New</option><option>In Review</option><option>Quoted</option><option>Confirmed</option><option>Closed</option>
                  </select>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="ghost-btn tiny"
                      style={{
                        display: "inline-flex",
                        visibility: "visible",
                        opacity: 1,
                        position: "relative",
                        zIndex: 2,
                        background: "#c8a24b",
                        color: "#1a1408",
                        border: "1px solid #c8a24b",
                        padding: "8px 14px",
                        minWidth: "64px",
                        cursor: "pointer"
                      }}
                      onClick={() => openEditOrder(o)}
                      data-testid={`edit-order-${o.id}`}
                    >
                      Edit Order
                    </button>
                    {isAdmin && (
                      <>
                        <button
                          type="button"
                          className="ghost-btn tiny"
                          onClick={() => openOrderHistory(o)}
                          data-testid={`order-history-${o.id}`}
                        >
                          History
                        </button>
                        <button
                          className="danger-btn small"
                          onClick={() => deleteOne(o.id)}
                          data-testid={`delete-order-${o.id}`}
                          aria-label={`Delete order ${o.id}`}
                        >
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
{isAdmin && showDeletedOrders && (
  <section className="orders" data-testid="deleted-orders-section">
    <div className="section-head">
      <div>
        <p className="eyebrow">Admin only</p>
        <h2>Deleted Orders ({deletedOrders.length})</h2>
      </div>
    </div>

    {loadingDeletedOrders ? (
      <p>Loading deleted orders...</p>
    ) : deletedOrders.length === 0 ? (
      <div className="empty-orders">
        <Trash2 size={30} />
        <p>No deleted orders found.</p>
      </div>
    ) : (
      <div className="order-table">
        <div className="table-head">
          <span>Order ID</span>
          <span>Customer</span>
          <span>Total</span>
          <span>Deleted on</span>
          <span>Action</span>
        </div>

        {deletedOrders.map((item) => {
          const order = item.data || {};

          return (
            <div className="table-row" key={item.trash_id}>
              <span><b>{order.id || "—"}</b></span>
              <span>
                {order.customer_name || "—"}
                <small>{order.phone || ""}</small>
              </span>
              <span><b>{money(order.total ?? 0)}</b></span>
              <span>
                {item.deleted_at
                  ? new Date(item.deleted_at).toLocaleString()
                  : "—"}
              </span>
              <span>
                <button
                  className="ghost-btn tiny"
                  onClick={() => restoreDeletedOrder(item.trash_id)}
                  data-testid={`restore-order-${item.trash_id}`}
                >
                  Restore
                </button>
              </span>
            </div>
          );
        })}
      </div>
    )}
  </section>
)}
        {editingOrder && (
          <div
            className="modal-backdrop"
            style={{ zIndex: 1000, overflowY: "auto", padding: 20 }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget && !savingOrder) setEditingOrder(null);
            }}
          >
            <form
              className="confirm-box"
              onSubmit={saveOrderEdits}
              style={{ width: "min(760px, 100%)", maxHeight: "90vh", overflowY: "auto", textAlign: "left" }}
              data-testid="edit-order-form"
            >
              <h2>Edit Order</h2>
              <p>Order ID: <b>{editingOrder.id}</b></p>
              <p style={{ fontSize: 13, color: "#777" }}>
                Product and coupon changes may only be allowed for orders that have not been confirmed or closed.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                {[
                  ["customer_name", "Customer name"],
                  ["phone", "Mobile number"],
                  ["email", "Email"],
                  ["gst_number", "GSTIN"],
                  ["coupon_code", "Coupon code"],
                  ["address", "Address"],
                ].map(([key, label]) => (
                  <label key={key} style={{ display: "grid", gap: 5 }}>
                    <span>{label}</span>
                    {key === "address" ? (
                      <textarea
                        rows={3}
                        value={editDraft[key] || ""}
                        onChange={(e) => setEditDraft((d) => ({ ...d, [key]: e.target.value }))}
                      />
                    ) : (
                      <input
                        value={editDraft[key] || ""}
                        onChange={(e) => setEditDraft((d) => ({ ...d, [key]: e.target.value }))}
                      />
                    )}
                  </label>
                ))}
              </div>
              <div style={{ marginTop: 18 }}>
                <h3 style={{ marginBottom: 10 }}>Products in this order</h3>

                {editItems.length === 0 ? (
                  <p style={{ color: "#777" }}>No products added yet.</p>
                ) : (
                  <div style={{ display: "grid", gap: 10 }}>
                    {editItems.map((item, index) => (
                      <div
                        key={`${item.product_id || item.name || "item"}-${index}`}
                        className="order-edit-item-row"
                        style={{
                          display: "grid",
                          gridTemplateColumns: "minmax(0, 2fr) minmax(90px, 0.7fr) minmax(0, 1.2fr) auto",
                          gap: 14,
                          alignItems: "end",
                          padding: 16,
                          border: "1px solid #ddd",
                          borderRadius: 8,
                          boxSizing: "border-box",
                          width: "100%"
                        }}
                      >
                        <label style={{ display: "grid", gap: 5 }}>
                          <span>Product / Variant</span>
                          {(() => {
                            const product = catalogProducts.find((p) => String(p.id) === String(item.product_id));
                            const variants = product?.variants || [];
                            const selectedVariant = variants.findIndex((v, vi) =>
                              (item.variant_id && String(v.variant_id || "") === String(item.variant_id)) ||
                              (!item.variant_id && item.variant_sku && String(v.sku || "") === String(item.variant_sku)) ||
                              (!item.variant_id && !item.variant_sku && item.variant_model_number && String(v.model_number || v.model || "") === String(item.variant_model_number)) ||
                              (!item.variant_id && !item.variant_sku && !item.variant_model_number && Number(item.variant_index) === vi)
                            );
                            return variants.length ? (
                              <select
                                value={selectedVariant >= 0 ? variantKey(variants[selectedVariant], selectedVariant) : ""}
                                onChange={(e) => updateEditItem(index, "variant_id", e.target.value)}
                                required
                              >
                                <option value="" disabled>Select variant</option>
                                {variants.map((v, vi) => (
                                  <option key={variantKey(v, vi)} value={variantKey(v, vi)}>
                                    {[v.wattage, v.size, v.model_number || v.model, v.sku].filter(Boolean).join(" | ") || `Variant ${vi + 1}`} — {money(v.price || 0)}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input value={product?.name || item.name || ""} readOnly />
                            );
                          })()}
                          <small style={{ color: "#777" }}>
                            {item.variant_sku || item.variant_model_number || item.variant_wattage || ""}
                          </small>
                        </label>

                        <label style={{ display: "grid", gap: 5 }}>
                          <span>Quantity</span>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={item.qty ?? 1}
                            onChange={(e) => updateEditItem(index, "qty", e.target.value)}
                            required
                          />
                        </label>

                        <label style={{ display: "grid", gap: 5 }}>
                          <span>Price each (₹)</span>
                          <input
                            type="number"
                            value={item.price ?? 0}
                            readOnly
                            aria-readonly="true"
                          />
                          <small>Line total: {money((Number(item.qty) || 0) * (Number(item.price) || 0))}</small>
                        </label>

                        <button
                          type="button"
                          className="danger-btn small"
                          disabled={savingOrder}
                          onClick={() => setEditItems((items) => items.filter((_, i) => i !== index))}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  alignItems: "center",
                  marginTop: 12
                }}>
                  <select
                    value={newItemProductId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setNewItemProductId(id);
                      const product = catalogProducts.find((p) => String(p.id) === String(id));
                      const firstVariant = product?.variants?.[0];
                      setNewItemVariantId(firstVariant ? variantKey(firstVariant, 0) : "");
                    }}
                    style={{ flex: "1 1 220px", minWidth: 0 }}
                  >
                    <option value="">Select product to add</option>
                    {catalogProducts.map((product) => (
                      <option key={product.id} value={product.id}>{product.name}</option>
                    ))}
                  </select>
                  {(() => {
                    const product = catalogProducts.find((p) => String(p.id) === String(newItemProductId));
                    const variants = product?.variants || [];
                    return variants.length ? (
                      <select
                        value={newItemVariantId}
                        onChange={(e) => setNewItemVariantId(e.target.value)}
                        style={{ flex: "1 1 220px", minWidth: 0 }}
                      >
                        {variants.map((v, vi) => (
                          <option key={variantKey(v, vi)} value={variantKey(v, vi)}>
                            {[v.wattage, v.size, v.model_number || v.model, v.sku].filter(Boolean).join(" | ") || `Variant ${vi + 1}`} — {money(v.price || 0)}
                          </option>
                        ))}
                      </select>
                    ) : product ? (
                      <span style={{ alignSelf: "center" }}>Price: {money(product.price || 0)}</span>
                    ) : null;
                  })()}
                  <button
                    type="button"
                    className="ghost-btn"
                    disabled={savingOrder || !newItemProductId}
                    onClick={addEditItem}
                  >
                    + Add Item
                  </button>
                </div>

                <div style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 12,
                  fontSize: 16
                }}>
                  <b>Items subtotal: {money(editItems.reduce(
                    (sum, item) => sum + (Number(item.qty) || 0) * (Number(item.price) || 0),
                    0
                  ))}</b>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
                <button type="button" className="ghost-btn" disabled={savingOrder} onClick={() => setEditingOrder(null)}>
                  Cancel
                </button>
                <button type="submit" className="primary-btn" disabled={savingOrder}>
                  {savingOrder ? "Saving..." : "Save changes"}
                </button>
              </div>
            </form>
          </div>
        )}

        {isAdmin && historyOrder && (
          <div className="modal-backdrop" style={{ zIndex: 1001, overflowY: "auto", padding: 20 }}>
            <div className="confirm-box" style={{ width: "min(760px, 100%)", maxHeight: "90vh", overflowY: "auto", textAlign: "left" }}>
              <h2>Order History</h2>
              <p>Order ID: <b>{historyOrder.id}</b></p>
              {loadingHistory ? (
                <p>Loading history...</p>
              ) : orderHistory.length === 0 ? (
                <p>No recorded changes found for this order.</p>
              ) : (
                <div style={{ display: "grid", gap: 12 }}>
                  {orderHistory.map((entry, index) => (
                    <div key={entry.id || `${entry.timestamp || entry.created_at || "event"}-${index}`} style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12 }}>
                      <b>{entry.action || "UPDATE"}</b>
                      <small style={{ display: "block", marginTop: 4 }}>
                        By: {entry.actor_name || entry.actor_email || entry.user_name || entry.actor_id || "Staff"}
                      </small>
                      <small style={{ display: "block" }}>
                        When: {entry.timestamp || entry.created_at ? new Date(entry.timestamp || entry.created_at).toLocaleString() : "—"}
                      </small>
                      <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontSize: 12 }}>
                        {JSON.stringify({ before: entry.before, after: entry.after }, null, 2)}
                      </pre>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                <button className="primary-btn" onClick={() => setHistoryOrder(null)}>Close</button>
              </div>
            </div>
          </div>
        )}

        <section className="orders" data-testid="customers-section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Customer database</p>
              <h2>Customers ({customers.length})</h2>
            </div>
          </div>
          {customers.length === 0 ? (
            <div className="empty-orders"><Users size={30} /><p>No customers yet.</p></div>
          ) : (
            <div className="order-table customer-table">
              <div className="table-head">
                <span>Customer</span><span>Salesman</span><span>Confirmed</span><span>Pipeline</span><span>Confirmed sales</span><span>Last order</span>
              </div>
              {customers.map((c, i) => (
                <div className="table-row" key={i} data-testid={`customer-row-${c.phone || i}`}>
                  <span>
                    <b>{c.name || "—"}</b>
                    <small>{c.phone}</small>
                    {c.email ? <small>{c.email}</small> : null}
                  </span>
                  <span>{salesName(c.advisor)}</span>
                  <span data-testid={`customer-confirmed-${c.phone || i}`}><b>{c.confirmed}</b><small>inquiries</small></span>
                  <span data-testid={`customer-pipeline-${c.phone || i}`}><b>{c.pipeline_count}</b><small>{money(c.pipeline_sales)}</small></span>
                  <span><b>{money(c.confirmed_sales)}</b></span>
                  <span>{c.last_order_at ? new Date(c.last_order_at).toLocaleDateString() : "—"}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function ProductEditModal({ product, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const newVariantId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const [variants, setVariants] = useState((product.variants || []).map((v) => ({
    variant_id: v.variant_id || newVariantId(),
    wattage: v.wattage || "",
    price: v.price ?? 0,
    size: v.size || "",
    stock: v.stock ?? 0,
    model_number: v.model_number || v.model || "",
    sku: v.sku || "",
  })));
  const addVariant = () => setVariants((v) => [...v, { variant_id: newVariantId(), wattage: "", price: 0, size: "", stock: 0, model_number: "", sku: "" }]);
  const updateVariant = (i, k, val) =>
    setVariants((v) => v.map((x, j) => (j === i ? { ...x, [k]: k === "price" || k === "stock" ? Number(val) : val } : x)));
  const removeVariant = (i) => setVariants((v) => v.filter((_, j) => j !== i));
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const form = e.currentTarget;
    const f = new FormData(form);
    const name = (f.get("name") || "").trim();
    if (!name) { setError("Product name is required"); return; }
    const cleanVariants = variants
      .filter((v) => (v.wattage || "").trim() || (v.size || "").trim() || (v.model_number || "").trim() || (v.sku || "").trim())
      .map((v) => ({
        variant_id: v.variant_id || newVariantId(),
        wattage: (v.wattage || "").trim(),
        price: Number(v.price) || 0,
        size: (v.size || "").trim(),
        stock: Math.max(0, Number(v.stock) || 0),
        model_number: (v.model_number || v.model || "").trim(),
        sku: (v.sku || "").trim(),
      }));
    const patch = {
      name,
      category: f.get("category") || "",
      price: f.get("price") === "" ? 0 : Number(f.get("price")),
      stock: f.get("stock") === "" ? 0 : Number(f.get("stock")),
      wattage: f.get("wattage") || "",
      color: f.get("color") || "",
      size: f.get("size") || "",
      warranty: f.get("warranty") || "",
      description: f.get("description") || "",
      variants: cleanVariants,
    };
    setBusy(true);
    try {
      await api.patch(`/products/${product.id}`, patch);
      const photo = f.get("photo");
      if (photo && photo.size > 0) {
        const fd = new FormData();
        fd.append("file", photo);
        await api.post(`/products/${product.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      onSaved();
      onClose();
    } catch (x) {
      setError(x.response?.data?.detail || "Could not save changes");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="edit-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="drawer-head">
          <div>
            <p className="eyebrow">Edit product · only name is required</p>
            <h2>{product.name}</h2>
          </div>
          <button type="button" className="close" onClick={onClose} data-testid="edit-close-button"><X /></button>
        </div>
        <div className="edit-grid">
          <label>Name <small>*required</small><input name="name" required defaultValue={product.name} data-testid="edit-name-input" /></label>
          <label>Category<input name="category" defaultValue={product.category || ""} list="edit-cats" data-testid="edit-category-input" /></label>
          <datalist id="edit-cats">
            <option>Bulb</option><option>Downlight</option><option>Floodlight</option>
            <option>Panel Light</option><option>Strip Light</option><option>Tube Light</option><option>Spotlight</option>
          </datalist>
          <label>Base price (₹)<input name="price" type="number" min="0" defaultValue={product.price ?? 0} data-testid="edit-price-input" /></label>
          <label>Stock<input name="stock" type="number" min="0" defaultValue={product.stock ?? 0} data-testid="edit-stock-input" /></label>
          <label>Default wattage<input name="wattage" defaultValue={product.wattage || ""} data-testid="edit-wattage-input" /></label>
          <label>Colour temp<input name="color" defaultValue={product.color || ""} data-testid="edit-color-input" /></label>
          <label>Size / spec<input name="size" defaultValue={product.size || ""} data-testid="edit-size-input" /></label>
          <label>Warranty<input name="warranty" defaultValue={product.warranty || ""} data-testid="edit-warranty-input" /></label>
          <label className="admin-full">
            Replace photo <small>(optional)</small>
            <input name="photo" type="file" accept="image/*" data-testid="edit-photo-input" />
          </label>
          <label className="admin-full">Description<textarea name="description" defaultValue={product.description || ""} data-testid="edit-desc-input" /></label>

          <div className="admin-full variants-editor" data-testid="variants-editor">
            <div className="variants-head">
              <b>Product variants — Power, Price, Size, Stock, Model Number &amp; SKU</b>
              <button type="button" className="ghost-btn" onClick={addVariant} data-testid="add-variant-button">
                <Plus size={14} /> Add variant
              </button>
            </div>
            <p className="profile-subtle">Add each variant with its power, price, size, stock, model number and SKU. Customers can select the variant from the product card.</p>
            {variants.length === 0 && <small className="profile-subtle">No variants yet. Base price above is used.</small>}
            {variants.map((v, i) => (
              <div className="variant-row" key={v.variant_id || i} data-testid={`variant-row-${i}`}>
                <input value={v.wattage || ""} onChange={(e) => updateVariant(i, "wattage", e.target.value)} placeholder="Power e.g. 9W" data-testid={`variant-wattage-${i}`} />
                <input type="number" min="0" value={v.price ?? 0} onChange={(e) => updateVariant(i, "price", e.target.value)} placeholder="Price ₹" data-testid={`variant-price-${i}`} />
                <input value={v.size || ""} onChange={(e) => updateVariant(i, "size", e.target.value)} placeholder="Size e.g. 6 inch" data-testid={`variant-size-${i}`} />
                <input type="number" min="0" value={v.stock ?? 0} onChange={(e) => updateVariant(i, "stock", e.target.value)} placeholder="Stock" data-testid={`variant-stock-${i}`} />
                <input value={v.model_number || ""} onChange={(e) => updateVariant(i, "model_number", e.target.value)} placeholder="Model number" data-testid={`variant-model-${i}`} />
                <input value={v.sku || ""} onChange={(e) => updateVariant(i, "sku", e.target.value)} placeholder="SKU" data-testid={`variant-sku-${i}`} />
                <button type="button" className="danger-btn tiny" onClick={() => removeVariant(i)} data-testid={`remove-variant-${i}`}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>

          {error && <div className="error admin-full" data-testid="edit-error">{error}</div>}
          <button className="primary-btn admin-full" disabled={busy} data-testid="edit-submit-button">
            {busy ? "Saving…" : "Save changes"} <Check size={17} />
          </button>
        </div>
      </form>
    </div>
  );
}

function AdminCatalog({ user, onLogout }) {
  const [products, setProducts] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [changeChoices, setChangeChoices] = useState({});
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [advBusy, setAdvBusy] = useState(false);
  const [error, setError] = useState("");
  const [advError, setAdvError] = useState("");
  const [uploadingId, setUploadingId] = useState("");
  const [advisorDrawer, setAdvisorDrawer] = useState(null); // {advisor, orders}
  const [permsModal, setPermsModal] = useState(null); // {advisor, perms, templates}
  const [rankings, setRankings] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const nowForReport = new Date();
  const [reportYear, setReportYear] = useState(String(nowForReport.getFullYear()));
  const [reportMonth, setReportMonth] = useState(String(nowForReport.getMonth() + 1));
  const [reportStartDate, setReportStartDate] = useState("");
  const [reportEndDate, setReportEndDate] = useState("");
  const [reportStatus, setReportStatus] = useState("All");
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [reportBusy, setReportBusy] = useState(false);
  const [reportError, setReportError] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponError, setCouponError] = useState("");
  const salesmanPhotoInputs = useRef({});

  const refresh = () => api.get("/products").then((r) => setProducts(r.data));

  const loadMonthlyReport = async () => {
    setReportBusy(true);
    setReportError("");
    try {
      const response = await api.get("/admin/reports/monthly-sales", {
        params: {
          year: Number(reportYear),
          month: Number(reportMonth),
          ...(reportStartDate ? { start_date: reportStartDate } : {}),
          ...(reportEndDate ? { end_date: reportEndDate } : {}),
          status: reportStatus,
        },
      });
      setMonthlyReport(response.data);
    } catch (e) {
      setReportError(e.response?.data?.detail || "Could not load monthly report.");
      setMonthlyReport(null);
    } finally {
      setReportBusy(false);
    }
  };

  const exportMonthlyReportCsv = () => {
    if (!monthlyReport) return;
    const columns = [
      ["order_id", "Order ID"], ["order_date", "Order Date"],
      ["status", "Status"], ["customer_name", "Customer"],
      ["phone", "Phone"], ["email", "Email"], ["address", "Address"],
      ["gst_number", "GSTIN"], ["sales_person_name", "Salesperson"],
      ["coupon_code", "Coupon"], ["product_name", "Product"],
      ["variant_wattage", "Wattage"], ["variant_size", "Size"],
      ["variant_model_number", "Model Number"], ["variant_sku", "SKU"],
      ["quantity", "Quantity"], ["rate", "Rate"],
      ["line_subtotal", "Line Subtotal"], ["subtotal", "Order Subtotal"],
      ["discount", "Discount"], ["gst", "GST"], ["total", "Order Total"],
    ];
    const quote = (value) => {
      let text = value == null ? "" : String(value);
      if (/^[=+@]/.test(text) || /^\s*-/.test(text)) text = "'" + text;
      return '"' + text.replace(/"/g, '""') + '"';
    };
    const lines = [
      columns.map(([, label]) => quote(label)).join(","),
      ...monthlyReport.rows.map((row) =>
        columns.map(([key]) => quote(row[key])).join(",")
      ),
    ];
    const blob = new Blob(["\\uFEFF" + lines.join("\\r\\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `luminest-sales-${reportStartDate || `${reportYear}-${String(reportMonth).padStart(2, "0")}`}-to-${reportEndDate || "end"}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };
  const downloadSalesReportFile = async (format) => {
    if (!monthlyReport) return;
    setReportBusy(true);
    setReportError("");
    try {
      const response = await api.get(`/admin/reports/monthly-sales.${format}`, {
        params: {
          year: Number(reportYear),
          month: Number(reportMonth),
          ...(reportStartDate ? { start_date: reportStartDate } : {}),
          ...(reportEndDate ? { end_date: reportEndDate } : {}),
          status: reportStatus,
        },
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `luminest-sales-${reportStartDate || `${reportYear}-${String(reportMonth).padStart(2, "0")}`}-to-${reportEndDate || "end"}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      let detail = e?.message || "Unknown error";
      const data = e?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          if (text) {
            try {
              const parsed = JSON.parse(text);
              detail = parsed.detail || parsed.message || text;
            } catch {
              detail = text.slice(0, 300);
            }
          }
        } catch {}
      } else if (data) {
        detail = data.detail || data.message || detail;
      }
      const status = e?.response?.status;
      setReportError(`Could not download ${format.toUpperCase()} report${status ? ` (HTTP ${status})` : ""}: ${String(detail).slice(0, 300)}`);
    } finally {
      setReportBusy(false);
    }
  };

  const refreshAdvisors = (from = dateFrom, to = dateTo) => {
    const params = new URLSearchParams();
    if (from) params.append("date_from", from);
    if (to) params.append("date_to", to);
    const qs = params.toString() ? `?${params.toString()}` : "";
    return api.get(`/advisors/stats${qs}`).then((r) => setAdvisors(r.data)).catch(() => {});
  };
  const refreshChangeRequests = () =>
    api.get("/admin/advisor-change-requests").then((r) => setChangeRequests(r.data)).catch(() => {});

  useEffect(() => {
    refresh().catch(() => {});
    refreshAdvisors();
    refreshChangeRequests();
    loadRankings();
    api.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAdvisorOrders = async (a) => {
    try {
      const r = await api.get(`/admin/advisors/${a.id}/orders`);
      setAdvisorDrawer({ advisor: a, orders: r.data });
    } catch (e) {
      alert(e.response?.data?.detail || "Could not load advisor orders");
    }
  };

  const uploadSalesmanPhoto = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/salesmen/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      refreshAdvisors();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not upload photo");
    }
  };

  const openPermissions = async (a) => {
    try {
      const r = await api.get(`/salesmen/${a.id}/permissions`);
      setPermsModal({ advisor: a, perms: r.data.permissions, templates: r.data.templates });
    } catch (e) {
      alert(e.response?.data?.detail || "Could not load permissions");
    }
  };

  const saveSalesmanPermissions = async (perms, template = "") => {
    try {
      await api.patch(`/salesmen/${permsModal.advisor.id}/permissions`, {
        permissions: perms, template,
      });
      setPermsModal(null);
    } catch (e) {
      alert(e.response?.data?.detail || "Could not save permissions");
    }
  };

  const loadRankings = async () => {
    try {
      const r = await api.get("/admin/customers/rankings");
      setRankings(r.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    const handleRankingsRefresh = () => {
      loadRankings();
    };
    window.addEventListener("luminest-rankings-refresh", handleRankingsRefresh);
    return () => {
      window.removeEventListener("luminest-rankings-refresh", handleRankingsRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const advisorName = (id) => (advisors.find((a) => a.id === id) || {}).name || id || "—";

  const approveChangeRequest = async (userId) => {
    const newAdvisorId = changeChoices[userId] ?? "";
    try {
      await api.post(`/admin/advisor-change-requests/${userId}/approve`, {
        new_advisor_id: newAdvisorId,
      });
      refreshChangeRequests();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not approve request");
    }
  };

  const rejectChangeRequest = async (userId) => {
    if (!window.confirm("Reject this advisor change request?")) return;
    try {
      await api.post(`/admin/advisor-change-requests/${userId}/reject`);
      refreshChangeRequests();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not reject request");
    }
  };

  const refreshCoupons = async () => {
    try { const r = await api.get("/admin/coupons"); setCoupons(r.data); }
    catch (e) { setCouponError(e.response?.data?.detail || "Could not load coupons"); }
  };

  const generateCouponCode = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let code = "LUMI-";
    for (let i = 0; i < 8; i++) code += chars[Math.floor(Math.random() * chars.length)];
    return code;
  };

  const createCoupon = async (e) => {
    e.preventDefault();
    setCouponError("");
    const f = new FormData(e.currentTarget);
    setCouponBusy(true);
    try {
      await api.post("/admin/coupons", {
        code: f.get("code"),
        discount_percent: Number(f.get("discount_percent")),
        expires_at: f.get("expires_at") ? new Date(f.get("expires_at")).toISOString() : "",
        usage_limit: Number(f.get("usage_limit") || 0),
        active: true,
      });
      e.currentTarget.reset();
      await refreshCoupons();
    } catch (x) {
      setCouponError(x.response?.data?.detail || "Could not create coupon");
    } finally { setCouponBusy(false); }
  };

  const toggleCoupon = async (coupon) => {
    try {
      await api.patch(`/admin/coupons/${coupon.id}`, { active: !coupon.active });
      await refreshCoupons();
    } catch (e) { alert(e.response?.data?.detail || "Could not update coupon"); }
  };

  const deleteCoupon = async (coupon) => {
    if (!window.confirm(`Delete coupon ${coupon.code}?`)) return;
    try { await api.delete(`/admin/coupons/${coupon.id}`); await refreshCoupons(); }
    catch (e) { alert(e.response?.data?.detail || "Could not delete coupon"); }
  };

  const applyDateFilter = (e) => {
    e.preventDefault();
    refreshAdvisors(dateFrom, dateTo);
  };
  const clearDateFilter = () => {
    setDateFrom(""); setDateTo("");
    refreshAdvisors("", "");
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this product from the catalog?")) return;
    try { await api.delete(`/products/${id}`); refresh(); } catch (e) {
      alert(e.response?.data?.detail || "Could not delete");
    }
  };

  const uploadPhoto = async (productId, file) => {
    if (!file) return;
    setUploadingId(productId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/products/${productId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not upload photo");
    } finally {
      setUploadingId("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const form = e.currentTarget;
    const f = new FormData(form);
    const photo = f.get("photo");
    const payload = {
      name: (f.get("name") || "").trim(),
      category: f.get("category") || "",
      price: f.get("price") === "" ? 0 : Number(f.get("price")),
      wattage: f.get("wattage") || "",
      color: f.get("color") || "",
      size: f.get("size") || "",
      warranty: f.get("warranty") || "",
      stock: f.get("stock") === "" ? 0 : Number(f.get("stock")),
      image: "",
      description: f.get("description") || "",
    };
    if (!payload.name) { setError("Product name is required"); return; }
    setBusy(true);
    try {
      const created = await api.post("/products", payload);
      if (photo && photo.size > 0) {
        const fd = new FormData();
        fd.append("file", photo);
        await api.post(`/products/${created.data.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      form.reset();
      await refresh();
    } catch (x) {
      setError(x.response?.data?.detail || "Could not add product");
    } finally {
      setBusy(false);
    }
  };

  const addAdvisor = async (e) => {
    e.preventDefault();
    setAdvError("");
    const form = e.currentTarget;
    const f = new FormData(form);
    setAdvBusy(true);
    try {
      await api.post("/advisors", {
        name: f.get("name"),
        email: f.get("email"),
        password: f.get("password"),
        specialty: f.get("specialty"),
        phone: f.get("phone") || "",
      });
      form.reset();
      await refreshAdvisors();
    } catch (x) {
      setAdvError(x.response?.data?.detail || "Could not add salesman");
    } finally {
      setAdvBusy(false);
    }
  };

  const changeAdvisorPassword = async (a) => {
  const password = window.prompt(
    `Enter a new password for ${a.name} (minimum 6 characters):`
  );

  if (password === null || password === "") return;

  if (password.length < 6) {
    alert("Password must be at least 6 characters.");
    return;
  }

  const confirmPassword = window.prompt("Re-enter the new password:");

  if (confirmPassword === null) return;

  if (password !== confirmPassword) {
    alert("Passwords do not match.");
    return;
  }

  try {
    await api.patch(`/advisors/${a.id}/password`, { password });
    alert(`Password updated successfully for ${a.name}.`);
  } catch (e) {
    alert(
      e.response?.data?.detail ||
      "Could not update salesperson password."
    );
  }
};

  const removeAdvisor = async (id) => {
    if (!window.confirm("Remove this salesman and their login?")) return;
    try { await api.delete(`/advisors/${id}`); refreshAdvisors(); } catch (e) {
      alert(e.response?.data?.detail || "Could not remove salesman");
    }
  };

  return (
    <div className="sales-page">
      <Header
        cartCount={0}
        onCart={() => {}}
        user={user}
        onAuth={() => {}}
        onLogout={onLogout}
        onDashboard={() => (window.location.href = "/sales")}
        onAdmin={() => {}}
      />
      <main className="sales-main">
        <div className="sales-heading">
          <div>
            <p className="eyebrow">Host only</p>
            <h1>Catalog <em>manager.</em></h1>
            <p>Add or edit products, upload photos, and manage your salesmen.</p>
          </div>
          <Link to="/sales" className="primary-btn" data-testid="admin-view-inquiries-button">
            View inquiries <ArrowRight size={17} />
          </Link>
        </div>

        <section className="admin-form-wrap" data-testid="monthly-sales-report-panel">
          <h2 className="admin-h2">Date-wise sales report</h2>
          <p className="profile-subtle">
            Select a date range, or leave dates blank to view the selected month. Includes order, customer, product, discount and GST details.
          </p>
          <div style={{display:"flex",gap:12,alignItems:"end",flexWrap:"wrap",margin:"16px 0"}}>
            <label>From date
              <input type="date" value={reportStartDate}
                max={reportEndDate || undefined}
                onChange={(e) => setReportStartDate(e.target.value)} />
            </label>
            <label>To date
              <input type="date" value={reportEndDate}
                min={reportStartDate || undefined}
                onChange={(e) => setReportEndDate(e.target.value)} />
            </label>
            <label>Month (if dates blank)
              <select value={reportMonth} onChange={(e) => setReportMonth(e.target.value)}>
                {Array.from({length:12}, (_, i) => (
                  <option key={i + 1} value={String(i + 1)}>
                    {new Date(2000, i, 1).toLocaleString("en-IN", {month:"long"})}
                  </option>
                ))}
              </select>
            </label>
            <label>Year
              <input type="number" min="2000" max="2100" value={reportYear}
                onChange={(e) => setReportYear(e.target.value)} style={{width:110}} />
            </label>
            <label>Order status
              <select value={reportStatus} onChange={(e) => setReportStatus(e.target.value)}>
                {["All", "New", "In Review", "Quoted", "Confirmed", "Closed"].map((status) => (
                  <option key={status} value={status}>
                    {status === "All" ? "All Statuses" : status}
                  </option>
                ))}
              </select>
            </label>
            <button className="primary-btn" onClick={loadMonthlyReport}
              disabled={reportBusy || !reportYear || Number(reportYear) < 2000 || Number(reportYear) > 2100 || (reportStartDate && reportEndDate && reportStartDate > reportEndDate)}>
              {reportBusy ? "Loading…" : "Generate report"}
            </button>
            <button className="ghost-btn" onClick={exportMonthlyReportCsv}
              disabled={!monthlyReport || !monthlyReport.rows.length}>
              Export CSV
            </button>
            <button className="ghost-btn" onClick={() => downloadSalesReportFile("pdf")}
              disabled={!monthlyReport || reportBusy}>
              Download PDF
            </button>
            <button className="ghost-btn" onClick={() => downloadSalesReportFile("xlsx")}
              disabled={!monthlyReport || reportBusy}>
              Download Excel Workbook
            </button>
          </div>
          {reportError && <p className="error" role="alert">{reportError}</p>}
          {monthlyReport && (
            <>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12,margin:"16px 0"}}>
                {[
                  ["Orders", monthlyReport.totals.order_count],
                  ["Subtotal", money(monthlyReport.totals.subtotal)],
                  ["Discount", money(monthlyReport.totals.discount)],
                  ["GST", money(monthlyReport.totals.gst)],
                  ["Total", money(monthlyReport.totals.total)],
                  ["Confirmed sales", money(monthlyReport.totals.confirmed_total)],
                ].map(([label, value]) => (
                  <div key={label} style={{padding:14,border:"1px solid var(--border, #ddd)",borderRadius:10}}>
                    <div className="profile-subtle">{label}</div>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>
              <p className="profile-subtle">
                {monthlyReport.rows.length} product line items · Amounts are in INR.
                {reportStatus === "All"
                  ? " Totals include all order statuses; confirmed sales are shown separately."
                  : ` Showing ${reportStatus} orders only.`}
              </p>
              <div style={{overflowX:"auto",maxWidth:"100%"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                  <thead>
                    <tr>
                      {["Date","Order","Status","Customer","Phone","GSTIN","Salesperson","Coupon","Product / variant","Qty","Rate","Discount","GST","Total"].map((label) => (
                        <th key={label} style={{textAlign:"left",padding:9,borderBottom:"1px solid var(--border, #ddd)",whiteSpace:"nowrap"}}>{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyReport.rows.map((row, i) => (
                      <tr key={`${row.order_id}-${row.product_id}-${i}`}>
                        <td style={{padding:9,whiteSpace:"nowrap"}}>{fmtDate(row.order_date)}</td>
                        <td style={{padding:9,whiteSpace:"nowrap"}}>{row.order_id}</td>
                        <td style={{padding:9}}>{row.status}</td>
                        <td style={{padding:9}}>{row.customer_name}</td>
                        <td style={{padding:9}}>{row.phone}</td>
                        <td style={{padding:9}}>{row.gst_number || "—"}</td>
                        <td style={{padding:9}}>{row.sales_person_name || row.sales_person_id || "—"}</td>
                        <td style={{padding:9}}>{row.coupon_code || "—"}</td>
                        <td style={{padding:9,minWidth:180}}>
                          {row.product_name}
                          {[row.variant_wattage, row.variant_size, row.variant_model_number, row.variant_sku].filter(Boolean).join(" · ") || ""}
                        </td>
                        <td style={{padding:9}}>{row.quantity}</td>
                        <td style={{padding:9,whiteSpace:"nowrap"}}>{money(row.rate)}</td>
                        <td style={{padding:9,whiteSpace:"nowrap"}}>{money(row.discount)}</td>
                        <td style={{padding:9,whiteSpace:"nowrap"}}>{money(row.gst)}</td>
                        <td style={{padding:9,whiteSpace:"nowrap"}}>{money(row.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>

        <section className="admin-form-wrap" data-testid="advisor-change-requests-panel">
          <h2 className="admin-h2">
            <ShieldAlert size={20} style={{verticalAlign:"middle",marginRight:8}}/>
            Salesman change requests ({changeRequests.length})
          </h2>
          {changeRequests.length === 0 ? (
            <p className="profile-subtle" data-testid="no-advisor-change-requests">
              No pending requests. Customers can ask to switch their locked salesman from
              their profile.
            </p>
          ) : (
            <div className="change-request-list">
              {changeRequests.map((c) => (
                <div className="change-request" key={c.id} data-testid={`change-request-${c.id}`}>
                  <div>
                    <b>{c.name}</b>
                    <small>{c.email} · {c.phone || "no phone"}</small>
                    <p>
                      Current advisor: <b>{advisorName(c.locked_advisor_id)}</b>
                      {c.advisor_change_preferred ? (
                        <> · Prefers: <b>{advisorName(c.advisor_change_preferred)}</b></>
                      ) : null}
                    </p>
                    <p className="profile-subtle">
                      <i>“{c.advisor_change_reason || "No reason provided"}”</i>
                    </p>
                  </div>
                  <div className="change-request-actions">
                    <select
                      value={changeChoices[c.id] ?? c.advisor_change_preferred ?? ""}
                      onChange={(e) =>
                        setChangeChoices((s) => ({ ...s, [c.id]: e.target.value }))
                      }
                      data-testid={`change-request-advisor-${c.id}`}
                    >
                      <option value="">Unlock — let customer pick</option>
                      {advisors.map((a) => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                    <button
                      className="primary-btn"
                      onClick={() => approveChangeRequest(c.id)}
                      data-testid={`approve-change-${c.id}`}
                    >
                      Approve
                    </button>
                    <button
                      className="danger-btn"
                      onClick={() => rejectChangeRequest(c.id)}
                      data-testid={`reject-change-${c.id}`}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="admin-form-wrap" data-testid="advisors-panel">
          <h2 className="admin-h2"><Users size={20} style={{verticalAlign:"middle",marginRight:8}}/> Salesmen ({advisors.length})</h2>
          <form className="admin-form" onSubmit={addAdvisor}>
            <label>Full name<input name="name" required data-testid="advisor-name-input" /></label>
            <label>Specialty<input name="specialty" required placeholder="Residential lighting" data-testid="advisor-specialty-input" /></label>
            <label>Login email<input name="email" type="email" required data-testid="advisor-email-input" /></label>
            <label>Password<input name="password" type="password" required minLength="6" data-testid="advisor-password-input" /></label>
            <label className="admin-full">Phone <small>(optional)</small><input name="phone" data-testid="advisor-phone-input" /></label>
            {advError && <div className="error admin-full" data-testid="advisor-error">{advError}</div>}
            <button className="primary-btn admin-full" disabled={advBusy} data-testid="add-advisor-button">
              {advBusy ? "Adding…" : "Add salesman"} <UserPlus size={17} />
            </button>
          </form>

          <form className="date-filter" onSubmit={applyDateFilter} data-testid="advisor-date-filter">
            <label>From<input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="advisor-date-from" /></label>
            <label>To<input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="advisor-date-to" /></label>
            <button className="ghost-btn" type="submit" data-testid="advisor-date-apply">Apply</button>
            {(dateFrom || dateTo) && <button className="link-btn" type="button" onClick={clearDateFilter} data-testid="advisor-date-clear">Clear</button>}
            <span className="date-filter-hint">
              {dateFrom || dateTo ? `Showing ${dateFrom || "beginning"} → ${dateTo || "today"}` : "Showing all-time stats"}
            </span>
          </form>

          <div className="advisor-stats">
            {advisors.map((a) => (
              <div
                className="stat-card clickable"
                key={a.id}
                data-testid={`advisor-stat-${a.id}`}
                onClick={() => openAdvisorOrders(a)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openAdvisorOrders(a)}
              >
                <img
                  src={a.image}
                  alt={a.name}
                  onError={(e) => { if (e.currentTarget.src !== IMG_FALLBACK) e.currentTarget.src = IMG_FALLBACK; }}
                />
                <div>
                  <p className="eyebrow">{a.specialty}</p>
<b>{a.name}</b>
<small style={{ display: "block", marginTop: 4 }}>
  Login email: {a.email || "Email not found"}
</small>
                  <div className="stat-row">
                    <span><small>Inquiries</small><b>{a.total_inquiries}</b></span>
                    <span><small>Confirmed</small><b data-testid={`advisor-confirmed-${a.id}`}>{a.confirmed}</b></span>
                    <span><small>Confirmed sales</small><b data-testid={`advisor-confirmed-sales-${a.id}`}>{money(a.confirmed_sales)}</b></span>
                    <span><small>Pipeline</small><b data-testid={`advisor-pipeline-${a.id}`}>{a.pipeline_count}</b></span>
                    <span><small>Pipeline value</small><b>{money(a.pipeline_sales)}</b></span>
                  </div>
                  <small className="click-hint">Click card to see inquiries →</small>
                </div>
                <div className="advisor-admin-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="ghost-btn tiny"
                    onClick={() => salesmanPhotoInputs.current[a.id]?.click()}
                    data-testid={`salesman-photo-button-${a.id}`}
                  >
                    <Camera size={13} /> Photo
                  </button>
                  <input
                    type="file"
                    accept="image/*"
                    ref={(el) => (salesmanPhotoInputs.current[a.id] = el)}
                    hidden
                    onChange={(e) => uploadSalesmanPhoto(a.id, e.target.files?.[0])}
                    data-testid={`salesman-photo-input-${a.id}`}
                  />
                  <button
                    className="ghost-btn tiny"
                    onClick={() => openPermissions(a)}
                    data-testid={`salesman-perms-button-${a.id}`}
                  >
                    <KeyRound size={13} /> Permissions
                  </button>
                  <button
  className="ghost-btn tiny"
  onClick={() => changeAdvisorPassword(a)}
  data-testid={`salesman-password-button-${a.id}`}
>
  <KeyRound size={13} /> Change Password
</button>
                  <button
                  
                    className="danger-btn tiny"
                    onClick={() => removeAdvisor(a.id)}
                    data-testid={`remove-advisor-${a.id}`}
                  >
                    <Trash2 size={13} /> Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="admin-form-wrap" data-testid="customer-rankings-panel">
          <h2 className="admin-h2">
            <Sparkles size={20} style={{verticalAlign:"middle",marginRight:8}}/>
            Top customers by confirmed value
          </h2>
          {rankings.length === 0 ? (
            <p className="profile-subtle" data-testid="no-rankings">No confirmed orders yet. Rankings appear as you confirm inquiries.</p>
          ) : (
            <div className="rank-list">
              {rankings.map((r, i) => (
                <div className="rank-row" key={r.customer_id} data-testid={`rank-${i+1}`}>
                  <span className="rank-num">#{i + 1}</span>
                  <div>
                    <b>{r.name}</b>
                    <small>{r.email} · {r.phone || "no phone"} · last {fmtDate(r.last_order_at)}</small>
                  </div>
                  <div>
                    <small>Confirmed</small>
                    <b style={{textAlign:"center",display:"block"}}>{r.confirmed_orders}</b>
                  </div>
                  <span className="rank-value">{money(r.total_value)}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="admin-form-wrap" data-testid="coupons-panel">
          <h2 className="admin-h2"><Tag size={20} style={{verticalAlign:"middle",marginRight:8}}/> Customer coupons</h2>
          <p className="profile-subtle">Only the admin can create coupons. Give these codes to your salesmen; customers enter them at checkout.</p>
          <form className="admin-form" onSubmit={createCoupon}>
            <label>Coupon code<input name="code" required minLength="3" placeholder="LUMI-WELCOME20" data-testid="coupon-admin-code" /></label>
            <label>Discount %<input name="discount_percent" required type="number" min="0.01" max="100" step="0.01" placeholder="20" data-testid="coupon-admin-discount" /></label>
            <label>Expires <small>(optional)</small><input name="expires_at" type="datetime-local" data-testid="coupon-admin-expiry" /></label>
            <label>Usage limit <small>(0 = unlimited)</small><input name="usage_limit" type="number" min="0" step="1" defaultValue="0" data-testid="coupon-admin-limit" /></label>
            {couponError && <div className="error admin-full" data-testid="coupon-admin-error">{couponError}</div>}
            <div className="admin-full" style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <button type="button" className="ghost-btn" onClick={(e) => { const input = e.currentTarget.form.elements.code; input.value = generateCouponCode(); }} data-testid="generate-coupon-button">Generate code</button>
              <button className="primary-btn" disabled={couponBusy} data-testid="create-coupon-button">{couponBusy ? "Creating…" : "Create coupon"} <Tag size={15}/></button>
            </div>
          </form>
          <div className="admin-list" style={{marginTop:16}}>
            {coupons.length === 0 ? <p className="profile-subtle">No coupons created yet.</p> : coupons.map((c) => (
              <div className="admin-row" key={c.id} data-testid={`coupon-row-${c.id}`}>
                <div>
                  <b style={{letterSpacing:1}}>{c.code}</b>
                  <small>{c.discount_percent}% off · Used {c.used_count || 0}{c.usage_limit ? ` / ${c.usage_limit}` : " · unlimited"}</small>
                  <small>{c.expires_at ? `Expires ${fmtDate(c.expires_at)}` : "No expiry"} · {c.active ? "Active" : "Inactive"}</small>
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <button className="ghost-btn tiny" onClick={() => toggleCoupon(c)}>{c.active ? "Deactivate" : "Activate"}</button>
                  <button className="danger-btn tiny" onClick={() => deleteCoupon(c)}><Trash2 size={13}/> Delete</button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="admin-form-wrap">
          <h2 className="admin-h2">Add a product <small style={{fontSize:11,color:"var(--muted)",marginLeft:8}}>only the name is required</small></h2>
          <form className="admin-form" onSubmit={submit}>
            <label>Name <small>*required</small><input name="name" required data-testid="admin-name-input" /></label>
            <label>Category<input name="category" list="admin-cats" data-testid="admin-category-input" /></label>
            <datalist id="admin-cats">
              <option>Bulb</option><option>Downlight</option><option>Floodlight</option>
              <option>Panel Light</option><option>Strip Light</option><option>Tube Light</option><option>Spotlight</option>
            </datalist>
            <label>Price (₹)<input name="price" type="number" step="1" min="0" data-testid="admin-price-input" /></label>
            <label>Stock<input name="stock" type="number" min="0" data-testid="admin-stock-input" /></label>
            <label>Wattage<input name="wattage" data-testid="admin-wattage-input" /></label>
            <label>Colour temp<input name="color" placeholder="Warm White 3000K" data-testid="admin-color-input" /></label>
            <label>Size / spec<input name="size" data-testid="admin-size-input" /></label>
            <label>Warranty<input name="warranty" placeholder="2 years" data-testid="admin-warranty-input" /></label>
            <label className="admin-full">
              Product photo <small>(JPG / PNG / WEBP, up to 5&nbsp;MB — optional, category icon shown if skipped)</small>
              <input name="photo" type="file" accept="image/*" data-testid="admin-photo-input" />
            </label>
            <label className="admin-full">Description<textarea name="description" data-testid="admin-desc-input" /></label>
            {error && <div className="error admin-full" data-testid="admin-error">{error}</div>}
            <button className="primary-btn admin-full" disabled={busy} data-testid="admin-submit-button">
              {busy ? "Adding…" : "Add product"} <Plus size={17} />
            </button>
          </form>
          <p className="profile-subtle" style={{marginTop:10}}>Tip: after creating a product, click <b>Edit</b> to add wattage/price variants.</p>
        </section>

        <section className="orders">
          <div className="section-head">
            <div>
              <p className="eyebrow">Current catalog</p>
              <h2>All products ({products.length})</h2>
            </div>
          </div>
          <div className="admin-list">
            {products.map((p) => (
              <div className="admin-row" key={p.id} data-testid={`admin-row-${p.id}`}>
                <img
                  src={imgUrl(p.image)}
                  alt=""
                  onError={(e) => { if (e.currentTarget.src !== IMG_FALLBACK) e.currentTarget.src = IMG_FALLBACK; }}
                />
                <div>
                  <p className="eyebrow">{p.category}</p>
                  <b>{p.name}</b>
                  <small>{money(p.price)} · Stock {p.stock}</small>
                </div>
                <div className="admin-row-actions">
                  <button className="ghost-btn" onClick={() => setEditing(p)} data-testid={`edit-product-${p.id}`}>
                    <Edit3 size={15} /> Edit
                  </button>
                  <label className="ghost-btn" data-testid={`admin-replace-photo-${p.id}`}>
                    <Camera size={15} /> {uploadingId === p.id ? "Uploading…" : "Photo"}
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={(e) => uploadPhoto(p.id, e.target.files?.[0])}
                    />
                  </label>
                  <button className="danger-btn" onClick={() => remove(p.id)} data-testid={`admin-delete-${p.id}`}>
                    <Trash2 size={15} /> Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
      {editing && (
        <ProductEditModal
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
      {permsModal && (
        <PermissionsModal
          state={permsModal}
          onSave={saveSalesmanPermissions}
          onClose={() => setPermsModal(null)}
        />
      )}
      {advisorDrawer && (
        <div className="modal-backdrop" onClick={() => setAdvisorDrawer(null)}>          <div
            className="advisor-drawer"
            onClick={(e) => e.stopPropagation()}
            data-testid="advisor-orders-drawer"
          >
            <button
              className="close"
              onClick={() => setAdvisorDrawer(null)}
              data-testid="advisor-orders-close"
            >
              <X />
            </button>
            <div className="drawer-head">
              <div>
                <p className="eyebrow">{advisorDrawer.advisor.specialty}</p>
                <h2>{advisorDrawer.advisor.name}</h2>
                <p className="profile-subtle">
                  {advisorDrawer.orders.length} inquiries · {" "}
                  {advisorDrawer.orders.filter(o=>o.status==="Confirmed").length} confirmed · {" "}
                  {advisorDrawer.orders.filter(o=>o.status==="New").length} new
                </p>
              </div>
            </div>
            {advisorDrawer.orders.length === 0 ? (
              <p className="profile-subtle" data-testid="advisor-orders-empty">
                No inquiries yet for this advisor.
              </p>
            ) : (
              <div className="advisor-order-list">
                {advisorDrawer.orders.map((o) => (
                  <div className="advisor-order" key={o.id} data-testid={`advisor-order-${o.id}`}>
                    <div>
                      <b>{o.customer_name}</b>
                      <small>{o.phone}</small>
                      {o.email ? <small>{o.email}</small> : null}
                    </div>
                    <div>
                      <b>{o.id}</b>
                      <small>{fmtDate(o.created_at)}</small>
                    </div>
                    <div>
                      <span className={`pill status-${(o.status || "").toLowerCase().replace(" ","-")}`}>
                        {o.status}
                      </span>
                      <b>{money(o.total ?? 0)}</b>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("luminest_cart") || "[]"); }
    catch { return []; }
  });
  const [user, setUser] = useState(null);
  const [auth, setAuth] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [sales, setSales] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [toasts, setToasts] = useState([]);
  const pushToast = (text, kind = "ok") => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 2600);
  };

  useEffect(() => {
    localStorage.setItem("luminest_cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    api.get("/sales-people").then((r) => setSales(r.data)).catch(() => {});
    // Emergent Google Auth returns to <origin>/#session_id=... — exchange it once.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH.
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (match) {
      setGoogleBusy(true);
      // Strip the fragment immediately so a refresh doesn't retry.
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      api.post("/auth/google/callback", { session_id: decodeURIComponent(match[1]) })
        .then((r) => setUser(r.data))
        .catch(() => alert("Google sign-in failed. Please try again."))
        .finally(() => { setGoogleBusy(false); setLoaded(true); });
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const doLogout = () => {
    api.post("/auth/logout").finally(() => {
      setUser(null);
      setConfirmSignOut(false);
    });
  };
  const askSignOut = () => setConfirmSignOut(true);
  const openProfile = () => setProfileOpen(true);

  if (!loaded) return null;

  if (googleBusy) {
    return (
      <div className="google-loading" data-testid="google-loading">
        <div className="google-spinner" />
        <p>Signing you in with Google…</p>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/sales"
          element={
            user?.role === "sales" || user?.role === "admin"
              ? <SalesDashboard user={user} onLogout={askSignOut} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/admin"
          element={
            user?.role === "admin"
              ? <AdminCatalog user={user} onLogout={askSignOut} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="*"
          element={
            <Home
              cart={cart}
              setCart={setCart}
              user={user}
              onAuth={() => setAuth(true)}
              onLogout={askSignOut}
              onProfile={openProfile}
              onOrders={() => setOrdersOpen(true)}
              toast={pushToast}
            />
          }
        />
      </Routes>
      {auth && <Auth setUser={setUser} onClose={() => setAuth(false)} />}
      {profileOpen && user && (
        <ProfileModal
          user={user}
          setUser={setUser}
          sales={sales}
          onClose={() => setProfileOpen(false)}
        />
      )}
      {ordersOpen && user && (
        <MyOrdersModal onClose={() => setOrdersOpen(false)} />
      )}
      {confirmSignOut && (
        <ConfirmDialog
          title="Sign out?"
          message="You'll need to sign in again to continue managing your orders."
          confirmLabel="Sign out"
          danger
          onConfirm={doLogout}
          onCancel={() => setConfirmSignOut(false)}
        />
      )}
      <ToastStack toasts={toasts} />
    </BrowserRouter>
  );
}

export default App;
